import {DataTable} from "../DataTable/DataTable";
import {compareString, PersonEntity} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {Anchor, Badge, Box, Modal, ModalProps} from "@mantine/core";
import {Link} from "react-router-dom";
import {usePagination} from "../DataTable/usePagination";
import {getColumnFilterProps} from "../DataTable/useDataTableFilter";
import {usePersonDataTableFilter} from "../PersonDataTable/usePersonDataTableFilter";
import {useDataTableSorter} from "../DataTable/useDataTableSorter";
import {EntityDataTableProps} from "../EntityDataTable/EntityDataTable";
import {useMemo} from "react";
import {DataTableColumn} from "mantine-datatable";
import { FormattedDatabaseCode } from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {sortBy} from "lodash";

const EMPTY_ARRAY: PersonEntity[] = []

export type PersonDataTableProps = EntityDataTableProps<PersonEntity> & {
  showStatus?: boolean
}

export const PersonDataTable = (props: PersonDataTableProps) => {
  const {
    records: persons = EMPTY_ARRAY,
    recordsPerPageOptions,
    showStatus = false,
    ...rest
  } = props

  const {t} = useTranslation(["common"]);

  const {filteredRecords, filterConfig} = usePersonDataTableFilter({
    records: persons
  })

  const sortConfig = useMemo(() => {
    return {
      "firstName" : {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.firstName?.toLowerCase()
          }])
        }
      },
      "lastName" : {
        sortAll: (records, columnAccessor) => {
          return sortBy(records, [function (record) {
            return record.lastName?.toLowerCase()
          }])
        }
      }
    }
  }, [])

  const {
    sortedRecords,
    sortStatus,
    onSortStatusChange,
  } = useDataTableSorter({
    records: filteredRecords,
    sortStatus: {columnAccessor: 'firstName', direction: 'asc'},
    sortConfig: sortConfig,
  })

  const pagination = usePagination({
    data: sortedRecords,
    recordsPerPageOptions: recordsPerPageOptions,
  });

  const columns = useMemo(() => {
    const values: DataTableColumn<PersonEntity>[] = [
      {
        ...getColumnFilterProps(filterConfig, "firstName"),
        title: t("common.entity.person.table.column.firstname", "First Name"),
        accessor: 'firstName',
        width: 100,
        sortable: true,
        render: ({id, firstName}) => (
          <Anchor component={Link} to={`/persons/profile?id=${id}`}>
            {firstName}
          </Anchor>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "lastName"),
        title: t("common.entity.person.table.column.firstname", "Last Name"),
        accessor: 'lastName',
        width: 100,
        sortable: true,
        render: ({id, lastName}) => (
          <Anchor component={Link} to={`/persons/profile?id=${id}`}>
            {lastName}
          </Anchor>
        ),
      },
      {
        ...getColumnFilterProps(filterConfig, "type"),
        title: t("common.entity.person.table.column.type", "Type"),
        accessor: 'type.code',
        width: 100,
        sortable: true,
        render: ({type}) => (
          <FormattedDatabaseCode value={type}/>
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "companyName"),
        title: t("common.entity.person.table.column.company", "Company"),
        accessor: 'companyName',
        width: 100,
        sortable: true,
        render: ({companyId, companyName}) => (
          companyId ? <DefaultText value={companyName}/> : undefined
        )
      },
      {
        ...getColumnFilterProps(filterConfig, "emailAddress"),
        title: t("common.entity.person.table.column.email", "Email"),
        accessor: 'emailAddress',
        width: 100,
        sortable: true,
      },
      {
        ...getColumnFilterProps(filterConfig, "barcode"),
        title: t("common.entity.person.table.column.qrcode", "QR Code"),
        accessor: 'barcode',
        width: 100,
        sortable: true,
      },
    ]

    if (showStatus) {
      values.push({
        title: '',
        accessor: "status",
        width: 50,
        textAlignment: 'right',
        render: ({state}) => {
          if (state && state.inShift) {
            return (
              <Badge color="green">Active</Badge>
            )
          }

          return undefined
        }
      })
    }

    return values
  }, [filterConfig, showStatus, t])

  return (
    <DataTable
      {...rest}
      {...(pagination ?? {})}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
      columns={columns}
    />
  )
}

export type PersonDataTableModalProps = Omit<ModalProps, "onSubmit"> & {
  dataTable: Omit<PersonDataTableProps, 'recordsPerPageOptions'>
}

export const PersonDataTableModal = (props: PersonDataTableModalProps) => {
  const {t} = useTranslation(["common"]);

  const {
    title = t<string>("common.entity.person.plural", "Persons"),
    dataTable,
    ...rest
  } = props;

  return (
    <Modal {...rest} title={title}>
      <Box>
        <PersonDataTable
          {...dataTable}
          recordsPerPageOptions={[5]}/>
      </Box>
    </Modal>
  );
};
